import React, { useState } from 'react'
import { useNavigate, Link } from 'react-router-dom'
import { useAuth } from '../../lib/auth.context'
import { supabase } from '../../lib/supabase'
import { Button } from '../../components/ui/button'
import { Input } from '../../components/ui/input'
import { Label } from '../../components/ui/label'
import { PasswordInput } from '../../components/ui/password-input'
import { Card } from '../../components/ui/card'
import { useToast } from '../../hooks/use-toast'
import { getThemeClass } from '../../lib/theme'
import { Alert, AlertDescription } from '../../components/ui/alert'
import { TOTPVerify } from '../../components/auth/totp-verify'
import { TOTPEnrollment } from '../../components/auth/totp-enrollment'


export function LoginPage() {
  const [email, setEmail] = useState('')
  const [password, setPassword] = useState('')
  const [formError, setFormError] = useState('')
  const [isLoading, setIsLoading] = useState(false)
  const [showTOTPVerify, setShowTOTPVerify] = useState(false)
  const [showTOTPEnroll, setShowTOTPEnroll] = useState(false)
  const [isUserBanned, setIsUserBanned] = useState(false)
  const { login } = useAuth()
  const navigate = useNavigate()
  const { toast } = useToast()

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault()
    setFormError('')
    setIsUserBanned(false)
    setIsLoading(true)
    try {
      await login(email, password)

      // Always check for MFA factors after successful password authentication
      const { data: factors } = await supabase.auth.mfa.listFactors()

      console.log('[Login] Password authentication successful, checking TOTP factors:', factors)

      // Check for verified TOTP factors only
      const verifiedTotpFactors = factors?.totp?.filter(factor => factor.status === 'verified') || []

      if (verifiedTotpFactors.length > 0) {
        // User has verified TOTP set up, require verification
        console.log('[Login] Verified TOTP factor found, requiring verification')
        setShowTOTPVerify(true)
      } else {
        // User needs to set up TOTP first (or complete incomplete enrollment)
        console.log('[Login] No verified TOTP factor found, requiring enrollment')
        setShowTOTPEnroll(true)
      }

      // Never redirect directly - always require MFA verification/enrollment
    } catch (error: unknown) {
      let errorMessage = 'Failed to login'

      // Check if this is a user_banned error
      if (error instanceof Error) {
        errorMessage = error.message
        // Check if the error message contains user_banned or parse JSON response
        try {
          const errorData = JSON.parse(error.message)
          if (errorData.code === 'user_banned') {
            setIsUserBanned(true)
          }
        } catch {
          // If error message is not JSON, check if it contains user_banned
          if (error.message.includes('user_banned') || error.message.includes('User is banned')) {
            setIsUserBanned(true)
          }
        }
      }

      setFormError(errorMessage)
      toast({
        title: 'Error',
        description: errorMessage,
        variant: 'destructive',
      })
    } finally {
      setIsLoading(false)
    }
  }

  return (
    <div className="min-h-screen bg-slate-950 flex items-center justify-center p-4">
      <div className="w-full max-w-5xl bg-slate-900 rounded-2xl border border-slate-700 shadow-2xl overflow-hidden">
        {showTOTPVerify ? (
        <div className="flex min-h-[500px]">
          {/* Left Panel - Marketing Content */}
          <div className="hidden lg:flex lg:w-1/2 relative overflow-hidden rounded-l-2xl">
            <div className="absolute inset-0 bg-cover bg-center bg-no-repeat" style={{ backgroundImage: `url(/fanfairpartnerlogin.jpg)` }}/>
            {/* Header */}
            <div className="absolute top-6 left-6 right-6 flex items-center justify-between z-10">
              <div className="flex items-center">
                <img src="/fanfairreversed.png" alt="FanFair" className="h-8"/>
              </div>
            </div>
            {/* Content */}
            <div className="relative z-10 w-full flex items-center justify-center p-12">
              <div className="text-center text-white max-w-md bg-black/60 p-10 border border-4 border-[#868686]/10 rounded-3xl">
                <h1 className="text-3xl font-bold mb-4 text-white">2FA Verification</h1>
                {/* Slide indicators */}
                <div className="flex justify-center space-x-2">
                  <div className="w-8 h-1 bg-white rounded-full"></div>
                  <div className="w-1 h-1 bg-white/40 rounded-full"></div>
                  <div className="w-1 h-1 bg-white/40 rounded-full"></div>
                </div>
              </div>
            </div>
          </div>
          {/* Right Panel - Verification Form */}
          <div className="w-full lg:w-1/2 bg-slate-800 flex items-center justify-center p-6 lg:p-12">
            <div className="w-full max-w-sm space-y-8">
              {/* Mobile Header */}
              <div className="lg:hidden text-center">
                <img src="/fanfairreversed.png" alt="FanFair" className="h-8 mx-auto"/>
              </div>
              <TOTPVerify redirectTo="/admin" />
            </div>
          </div>
        </div>
      ) : showTOTPEnroll ? (
        <TOTPEnrollment
          onComplete={async () => {
            console.log('[Login] TOTP enrollment complete, redirecting to admin dashboard')
            navigate('/admin', { replace: true })
          }}
        />
      ) : (
        <div className="flex min-h-[500px]">
          {/* Left Panel - Marketing Content */}
          <div className="hidden lg:flex lg:w-1/2 relative overflow-hidden rounded-l-2xl">
            <div className="absolute inset-0 bg-cover bg-center bg-no-repeat" style={{ backgroundImage: `url(/fanfairpartnerlogin.jpg)` }}/>
            {/* Header */}
            <div className="absolute top-6 left-6 right-6 flex items-center justify-between z-10">
              <div className="flex items-center">
                <img src="/fanfairreversed.png" alt="FanFair" className="h-8"/>
              </div>
            </div>
            {/* Content */}
            <div className="relative z-10 flex items-center justify-center p-12">
              <div className="text-center text-white max-w-md bg-black/60 p-10 border border-4 border-[#868686]/10 rounded-3xl">
                <h1 className="text-3xl font-bold mb-4 text-white">
                  Capturing Moments, Creating Memories
                </h1>
                <p className="text-lg opacity-90 mb-8 text-white">
                  Access your FanFair Admin Portal to manage events, view analytics, and grow your business.
                </p>
                {/* Slide indicators */}
                <div className="flex justify-center space-x-2">
                  <div className="w-8 h-1 bg-white rounded-full"></div>
                  <div className="w-1 h-1 bg-white/40 rounded-full"></div>
                  <div className="w-1 h-1 bg-white/40 rounded-full"></div>
                </div>
              </div>
            </div>
          </div>
          {/* Right Panel - Login Form */}
          <div className="w-full lg:w-1/2 bg-slate-800 flex items-center justify-center p-6 lg:p-12">
            <div className="w-full max-w-sm space-y-8">
              {/* Mobile Header */}
              <div className="lg:hidden text-center">
                <img src="/fanfairreversed.png" alt="FanFair" className="h-8 mx-auto"/>
              </div>
              <div className="space-y-2">
                <h1 className="text-2xl font-medium text-white">Sign in to your account</h1>
                <p className="text-slate-400 font-light">
                  Welcome back! Please enter your details.
                </p>
              </div>


              <form onSubmit={handleSubmit} className="space-y-6">
                {formError && (
                  <Alert variant="destructive" className="mt-4">
                    <AlertDescription className="text-white">{formError}</AlertDescription>
                  </Alert>
                )}
                <div className="space-y-4">
                  <div className="space-y-2">
                    <Label htmlFor="email" className="text-sm font-light text-white">
                      Email
                    </Label>
                    <Input
                      className="bg-slate-700 border-slate-600 text-white placeholder-slate-400 focus:border-primary focus:ring-primary/20"
                      placeholder="Enter your email"
                      value={email}
                      onChange={(e) => setEmail(e.target.value)}
                      required
                      disabled={isLoading}
                      type="email"
                    />
                  </div>
                  <div className="space-y-2">
                    <Label htmlFor="password" className="text-sm font-light text-white">
                      Password
                    </Label>
                    <div className="relative">
                      <PasswordInput
                        className="bg-slate-700 border-slate-600 text-white placeholder-slate-400 focus:border-primary focus:ring-primary/20 pr-10"
                        placeholder="Enter your password"
                        value={password}
                        onChange={(e) => setPassword(e.target.value)}
                        required
                        disabled={isLoading}
                      />
                    </div>
                  </div>
                </div>
                <div className="flex items-center justify-between">
                  <Link to="/auth/forgot-password" className="text-sm text-primary hover:underline">
                    Forgot password?
                  </Link>
                </div>
                <Button type="submit"  className="w-full bg-primary hover:bg-primary/90 text-primary-foreground" disabled={isLoading}>
                  {isLoading ? 'Signing in...' : 'Sign in'}
                </Button>
              </form>
            </div>
          </div>
        </div>
      )}
      </div>
    </div>


    // NOTE: To Be Remove after Login Credential issue
    // <div className={`flex h-screen w-full items-center justify-center ${getThemeClass('gradients.primary')}`}>
    //   {showTOTPVerify ? (
    //     <TOTPVerify
    //       redirectTo="/admin"
    //       onComplete={() => {
    //         console.log('[Login] TOTP verification complete, redirecting to admin dashboard')
    //         navigate('/admin', { replace: true })
    //       }}
    //     />
    //   ) : showTOTPEnroll ? (
    //     <TOTPEnrollment
    //       onComplete={async () => {
    //         console.log('[Login] TOTP enrollment complete, redirecting to admin dashboard')
    //         navigate('/admin', { replace: true })
    //       }}
    //     />
    //   ) : (
    //     <Card className={`w-full max-w-md p-6 ${getThemeClass('components.card.base')}`}>
    //       <h1 className={`mb-6 text-2xl font-bold ${getThemeClass('components.text.heading')}`}>Login</h1>
    //       <form onSubmit={handleSubmit} className="space-y-4">
    //         <div>
    //           <Input
    //             type="email"
    //             placeholder="Email"
    //             value={email}
    //             onChange={(e) => setEmail(e.target.value)}
    //             required
    //             disabled={isLoading}
    //             className={getThemeClass('components.input.base')}
    //           />
    //         </div>
    //         <div>
    //           <PasswordInput
    //             placeholder="Password"
    //             value={password}
    //             onChange={(e) => setPassword(e.target.value)}
    //             required
    //             disabled={isLoading}
    //             className={getThemeClass('components.input.base')}
    //           />
    //         </div>
    //         {formError && (
    //           <Alert variant="destructive" className="mt-4">
    //             <AlertDescription>{formError}</AlertDescription>
    //           </Alert>
    //         )}
    //         <Button
    //           type="submit"
    //           className={`w-full ${getThemeClass('components.button.primary')}`}
    //           disabled={isLoading}
    //         >
    //           {isLoading ? 'Logging in...' : 'Login'}
    //         </Button>
    //         {!isUserBanned && (
    //           <div className="mt-4 text-center">
    //             <Link to="/auth/forgot-password" className="text-sm text-primary hover:underline">
    //               Forgot your password?
    //             </Link>
    //           </div>
    //         )}
    //       </form>
    //     </Card>
    //   )}
    // </div>
  )
}
